export const SELECTED_PRIVATE_FILE_STORAGE_KEY = 'prospo_selected_private_file';

export interface ChatContext {
  sessionId: string;
  currentRoute: string;
  routeParams: Record<string, string>;
  currentUser?: any;
  currentClient?: any;
  currentMeeting?: any;
  selectedPrivateFile?: {
    attachmentId: string;
    fileName: string;
  } | null;
  timestamp: Date;
  resetSessionId: () => void;
  loadSessionId: (id: string) => void;
}

/** A write/destructive action the agent proposed and is waiting for the user to approve. */
export interface PendingAction {
  toolCallId: string;
  tool: string;
  summary: string;              // server-authored, human-readable
  params?: any;                 // display-friendly resolved details
  /** Multi-choice proposal — render one button per option instead of Approve/Decline. */
  options?: { label: string }[];
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolOperations?: ToolOperation[];
  isStreaming?: boolean;
  isNavigationFeedback?: boolean;
  /** When set, render an approval card under this message. Cleared once resolved. */
  pendingAction?: PendingAction | null;
  /** When set after a meeting is created, renders a "View Meeting" link in the bubble. */
  meetingLink?: { meetingId: string; title: string } | null;
  /** Reopens the exact client email draft navigation produced by the agent. */
  emailLink?: { route: string; state?: Record<string, string> } | null;
  /** When set after build_workflow succeeds, renders a "View Workflow" link in the bubble. */
  workflowLink?: { workflowId: string; workflowName: string } | null;
  /**
   * The agent called open_file_upload — render a button so the ACTUAL click
   * that opens the native file picker is a genuine user gesture. Browsers
   * refuse to open <input type="file"> from an async event dispatch (the
   * tool result arrives well outside any "user activation" window), so the
   * picker can never be opened automatically the moment this message lands.
   */
  needsFileUploadTrigger?: boolean;
  sessionId?: string;   // ponytail: tags messages with their origin session for expired-action detection
  attachments?: ChatAttachment[];
}

export interface ChatAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  status: 'uploading' | 'ready' | 'error';
  progress?: number;
}

export type AgentProviderName =
  | 'azure-openai'
  | 'openai'
  | 'anthropic'
  | 'gemini';

export interface AgentModelSelection {
  provider: AgentProviderName;
  model: string;
}

export interface AgentProviderOption {
  provider: AgentProviderName;
  label: string;
  configured: boolean;
  models: string[];
}

export interface ChatModelOptionsResponse {
  providers: AgentProviderOption[];
  defaultModel: AgentModelSelection;
  selectedModel: AgentModelSelection;
}

export interface SessionBlock {
  sessionId: string;
  title: string | null;   // AI-generated or user-renamed short title, null until generated
  preview: string;   // first user message text — sourced from getSessions() API response
  date: string;      // ISO date string from session updatedAt
  messages: Message[];
}

export type ActionType =
  | 'navigate'      // Navigate to a different page
  | 'openModal'     // Open a modal dialog
  | 'multiStep'     // Execute multiple actions in sequence
  | 'inChat'        // Display response in chat only
  | 'data'          // Return data without UI action
  | 'error';        // Error occurred

export interface ToolActionResult {
  success: boolean;                   // Whether the operation succeeded
  actionType: ActionType;             // Type of action to perform
  guidance?: string;                  // Human-readable guidance message

  // Navigation-specific fields
  route?: string;                     // Route to navigate to (for actionType: 'navigate')

  // Modal-specific fields  
  modalName?: string;                 // Modal component name (for actionType: 'openModal')
  modalParams?: any;                  // Parameters to pass to modal

  // Multi-step specific fields
  steps?: ToolActionResult[];                // Array of steps to execute (for actionType: 'multiStep')

  // Data fields (for other action types)
  data?: any;                         // Any data payload
  message?: string;                   // Status or result message
}

export interface ToolOperation {
  toolCallId: string;                 // Unique identifier for the tool call
  toolName: string;                   // Name of the tool that was executed
  arguments: any;                     // Arguments passed to the tool
  result: ToolActionResult;                  // Standardized result structure
  error?: string;                     // Error message if tool failed
  displayContent?: string;            // Human-readable description of what was executed
}

export interface ChatRequest {
  message: string;
  /** Clients explicitly @mentioned in this message — see ChatMessageInput.tsx. The
   *  backend re-resolves these server-side rather than trusting the name/id as-is. */
  mentionedClients?: { id: string; name: string }[];
  /** Shared/Private Files explicitly selected from the @ picker. */
  mentionedFiles?: { id: string; name: string; type: "shared-file" | "private-file" }[];
  /** File-note templates explicitly ~picked in this message — same re-resolution posture as mentionedClients. */
  mentionedTemplates?: { id: string; name: string }[];
  /** Skills explicitly selected from the slash picker in this message. */
  mentionedSkills?: { id: string; name: string }[];
  /** File-note rules explicitly ^picked (for removal) in this message — same re-resolution posture as mentionedClients. */
  mentionedRules?: { id: string; name: string }[];
  attachmentIds?: string[];
  context: ChatContext;
  history: Message[];
  streaming?: boolean;
  signal?: AbortSignal;
  sessionId?: string | null;
  /** Primary model for this conversation. The server persists it on the session. */
  modelSelection?: AgentModelSelection;
}

export interface ChatResponse {
  content: string;                    // AI's text response to user
  toolOperations?: ToolOperation[];     // Array of operations to execute
  sessionId?: string;                 // server-authoritative session id
  /**
   * The client this conversation is about — server-resolved and sticky for the
   * session, so it survives navigating away from that client's page. Null when
   * no client has been established yet. Drives the chat's "Client:" banner.
   */
  conversationClient?: { id: string; name: string } | null;
  pendingAction?: PendingAction | null; // present when awaiting confirmation
  meetingLink?: { meetingId: string; title: string } | null;
  emailLink?: { route: string; state?: Record<string, string> } | null;
  workflowLink?: { workflowId: string; workflowName: string } | null;
  modelSelection?: AgentModelSelection;
}
